import { useEffect, useMemo, useState } from "react";
import useGetUserNotifications from "api/Notifications/useGetUserNotifications";
import useMarkNotificationAsRead from "api/Notifications/useMarkNotificationAsRead";
import useMarkAllNotificationsAsRead from "api/Notifications/useMarkAllNotificationsAsRead";
import Container from "layouts/Container";
import PaddingX from "layouts/PaddingX";
import CustomPaginator from "components/CustomPaginator";

const STATUS = {
	UNREAD: "unread",
	ALL: "all",
	READ: "read",
};

const NotificationSkeleton = () => (
	<div className="space-y-3 p-4">
		{Array.from({ length: 6 }).map((_, index) => (
			<div key={index} className="border border-gray-100 rounded-lg p-4 animate-pulse">
				<div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
				<div className="h-3 bg-gray-100 rounded w-5/6 mb-2" />
				<div className="h-3 bg-gray-100 rounded w-1/2" />
			</div>
		))}
	</div>
);

const Notifications = () => {
	const [status, setStatus] = useState(STATUS.UNREAD);
	const [statusTouched, setStatusTouched] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [firstPage, setFirstPage] = useState(0);

	useEffect(() => {
		const timer = setTimeout(() => {
			setSearchTerm(searchInput.trim());
			setPage(1);
			setFirstPage(0);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchInput]);

	const queryParams = useMemo(
		() => ({
			status,
			searchTerm,
			from_date: fromDate,
			to_date: toDate,
			page,
			rows_per_page: rowsPerPage,
		}),
		[status, searchTerm, fromDate, toDate, page, rowsPerPage]
	);

	const { data, isLoading, isError, refetch } = useGetUserNotifications(queryParams);

	const notifications = data?.data?.records || [];
	const totalRecords = data?.data?.total_records || 0;
	const unreadCount = Number(data?.data?.unread_count ?? 0);
	const readCount = Number(data?.data?.read_count ?? 0);
	const allCount = Number(data?.data?.all_count ?? 0);
	const hasFilters = Boolean(searchTerm || fromDate || toDate);

	// Mark as read mutation
	const markAsRead = useMarkNotificationAsRead();
	const markAllAsRead = useMarkAllNotificationsAsRead();

	useEffect(() => {
		if (!statusTouched && status === STATUS.UNREAD && !isLoading && unreadCount === 0) {
			setStatus(STATUS.ALL);
		}
	}, [status, statusTouched, isLoading, unreadCount]);

	const handleRowClick = async (notif) => {
		if (!notif.was_read) {
			await markAsRead.mutateAsync({ id: notif.id });
		}

		if (notif.link) {
			window.location.href = notif.link;
		}
	};

	const handleStatusChange = (nextStatus) => {
		setStatus(nextStatus);
		setStatusTouched(true);
		setPage(1);
		setFirstPage(0);
	};

	const markAllDisabled = unreadCount === 0 || markAllAsRead.isLoading;
	const activeEmptyMessage =
		status === STATUS.UNREAD && !hasFilters
			? "You're all caught up."
			: "No notifications match these filters.";

	return (
		<Container>
			<div className="mt-10 flex-1 pb-12">
				<PaddingX>
					<div className="max-w-5xl mx-auto w-full">
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
							<div>
								<h2 className="text-2xl font-bold">My Notifications</h2>
								<p className="text-sm text-gray-500 mt-1">
									{unreadCount} unread of {allCount} total
								</p>
							</div>
							<button
								type="button"
								onClick={() => markAllAsRead.mutateAsync()}
								disabled={markAllDisabled}
								className="px-4 py-2 rounded-md border border-akgreen text-akgreen hover:bg-akgreen hover:text-white disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white disabled:hover:text-gray-400"
							>
								Mark all as read
							</button>
						</div>

						<div className="bg-white rounded-lg shadow border border-gray-100 p-4 mb-4">
							<div className="flex flex-wrap items-center gap-2 mb-4">
								{[
									{ key: STATUS.UNREAD, label: `Unread (${unreadCount})` },
									{ key: STATUS.ALL, label: `All (${allCount})` },
									{ key: STATUS.READ, label: `Read (${readCount})` },
								].map((tab) => (
									<button
										type="button"
										key={tab.key}
										onClick={() => handleStatusChange(tab.key)}
										className={`px-3 py-1.5 text-sm rounded-full border ${
											status === tab.key
												? "bg-akgreen text-white border-akgreen"
												: "bg-white border-gray-200 text-gray-700 hover:border-akgreen"
										}`}
									>
										{tab.label}
									</button>
								))}
							</div>

							<div className="grid grid-cols-1 md:grid-cols-4 gap-3">
								<input
									type="text"
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									placeholder="Search notifications"
									className="md:col-span-2 px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-akgreen"
								/>
								<input
									type="date"
									value={fromDate}
									onChange={(e) => {
										setFromDate(e.target.value);
										setPage(1);
										setFirstPage(0);
									}}
									className="px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-akgreen"
								/>
								<input
									type="date"
									value={toDate}
									onChange={(e) => {
										setToDate(e.target.value);
										setPage(1);
										setFirstPage(0);
									}}
									className="px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-akgreen"
								/>
							</div>
						</div>

						<div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
							{isLoading ? (
								<NotificationSkeleton />
							) : isError ? (
								<div className="p-8 text-center">
									<p className="text-gray-600 mb-3">Could not load notifications.</p>
									<button
										type="button"
										onClick={() => refetch()}
										className="px-3 py-1.5 rounded-md bg-akgreen text-white"
									>
										Retry
									</button>
								</div>
							) : notifications.length === 0 ? (
								<div className="p-8 text-center text-gray-500">{activeEmptyMessage}</div>
							) : (
								<>
									{/* Desktop Table */}
									<table className="min-w-full text-sm hidden lg:table">
										<thead>
											<tr className="bg-gray-50 text-gray-600">
												<th className="px-4 py-3 text-left font-semibold">Title</th>
												<th className="px-4 py-3 text-left font-semibold">Message</th>
												<th className="px-4 py-3 text-left font-semibold">Date</th>
												<th className="px-4 py-3 text-left font-semibold">Status</th>
											</tr>
										</thead>
										<tbody>
											{notifications.map((notif) => (
												<tr
													key={notif.id}
													className={`transition-colors cursor-pointer border-b last:border-b-0 ${
														notif.was_read
															? "bg-gray-50 hover:bg-gray-100"
															: "bg-white hover:bg-blue-50"
													}`}
													onClick={() => handleRowClick(notif)}
													title={notif.was_read ? "Read" : "Unread"}
												>
													<td className="px-4 py-3 font-medium max-w-xs truncate">
														<div className="flex items-center gap-2 min-w-0">
															{!notif.was_read && <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />}
															<span className="truncate">{notif.title}</span>
														</div>
													</td>
													<td className="px-4 py-3 text-gray-600 max-w-md truncate">{notif.message}</td>
													<td className="px-4 py-3 text-gray-500 whitespace-nowrap">{notif.ago}</td>
													<td className="px-4 py-3">
														{notif.was_read ? (
															<span className="inline-block px-2 py-1 rounded-full bg-gray-200 text-gray-600 text-xs">
																Read
															</span>
														) : (
															<span className="inline-block px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
																Unread
															</span>
														)}
													</td>
												</tr>
											))}
										</tbody>
									</table>

									{/* Mobile Cards */}
									<div className="lg:hidden flex flex-col gap-4 p-2">
										{notifications.map((notif) => (
											<div
												key={notif.id}
												className={`rounded-lg border transition-colors cursor-pointer shadow-sm p-4 flex flex-col gap-2 ${
													notif.was_read
														? "bg-gray-50 hover:bg-gray-100 border-gray-200"
														: "bg-white hover:bg-blue-50 border-blue-200"
												}`}
												onClick={() => handleRowClick(notif)}
												title={notif.was_read ? "Read" : "Unread"}
											>
												<div className="flex items-center justify-between gap-2">
													<div className="flex items-center gap-2 min-w-0">
														{!notif.was_read && <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />}
														<span className="font-semibold text-base truncate">{notif.title}</span>
													</div>
													{notif.was_read ? (
														<span className="inline-block px-2 py-1 rounded-full bg-gray-200 text-gray-600 text-xs">
															Read
														</span>
													) : (
														<span className="inline-block px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
															Unread
														</span>
													)}
												</div>
												<div className="text-gray-600 text-sm break-words">{notif.message}</div>
												<div className="flex items-center justify-between text-xs text-gray-500 mt-1">
													<span>{notif.ago}</span>
													{notif.link && <span className="text-blue-600 underline ml-2">View</span>}
												</div>
											</div>
										))}
									</div>
								</>
							)}
						</div>
						{totalRecords > 0 && (
							<div className="flex justify-end items-center mt-6">
								<CustomPaginator
									setPage={setPage}
									firstPage={firstPage}
									rowsPerPage={rowsPerPage}
									setFirstPage={setFirstPage}
									setRowsPerPage={setRowsPerPage}
									totalRecords={totalRecords}
								/>
							</div>
						)}
					</div>
				</PaddingX>
			</div>
		</Container>
	);
};

export default Notifications;
